import React, { useContext, useEffect } from 'react'
import { NavLink } from 'react-router'
import { css } from '@emotion/react'
import { colors } from 'shared-ui'
import FallbackMessage from '../_general/FallbackMessage'
import PageSubtitle from '../_general/PageSubtitle'
import PageTitle from '../_general/PageTitle'
import ClubContext from '../../context/Club'
import LoaderText from '../_general/LoaderText'
import Loader from '../_general/Loader'
import PitchCardWithData from '../pitch/PitchCardWithData'
import { FormattedMessage } from 'react-intl'
import useClubVenuePitches from '../../hooks/useClubVenuePitches'
import PreferredViewContext from '../../context/PreferredView'
import ProfileContext from '../../context/Profile'
import { getHashParams, setHashParams } from '../../utils/urlHashParams'
import ClubPitchListPerformance from './ClubPitchListPerformance'

export default function ClubPitchList({
    ...props
}) {

    const { data: club } = useContext(ClubContext)
    const { venuePitches, clubLogo, isBusy } = useClubVenuePitches()
    const { isPerformanceMode } = useContext(PreferredViewContext)

    const { data: profile } = useContext(ProfileContext)
    const allowPerformanceFeature = profile?.featureFlags?.includes('performance')
    const showPerformance = allowPerformanceFeature && isPerformanceMode

    // set a hash '#viewMode=performance|greenkeeping' to let analytics track each as a separate page.
    useEffect(() => {
        const hashState = getHashParams().viewMode
        const currentMode = showPerformance ? 'performance' : 'greenkeeping'
        if (hashState !== currentMode) setHashParams({ viewMode: currentMode })
    }, [showPerformance])

    const isEmptyState = Object.keys(venuePitches).length === 0

    const PageTitleWLogo = <PageTitleWithLogo club={club} clubLogo={clubLogo} />

    if (!profile) return null

    return (
        <div {...props} >
            {showPerformance ?
                <div css={css`max-width: 2200px;`}>
                    <ClubPitchListPerformance
                        club={club}
                        isBusy={isBusy}
                        isEmptyState={isEmptyState}
                        venuePitches={venuePitches}
                        PageTitleWithLogo={PageTitleWLogo}
                        sortPitches={sortPitches}
                        clubLogo={clubLogo}
                    />
                </div>
                :
                <ClubPitchListGreenkeeping
                    isBusy={isBusy}
                    isEmptyState={isEmptyState}
                    venuePitches={venuePitches}
                    PageTitleWithLogo={PageTitleWLogo}
                />
            }
        </div>
    )
}

function ClubPitchListGreenkeeping({ isBusy, isEmptyState, venuePitches, PageTitleWithLogo }) {
    return <>
        <div css={style.pageHeader}>
            {PageTitleWithLogo}
        </div>

        {isBusy ?
            <BusyStateUI />
            : isEmptyState ?
                <EmptyStateUi />
                : venuePitches.map(({ venueName, venue, pitches }, index) => {
                    return <React.Fragment key={venueName ?? index}>
                        <PageSubtitle>
                            {venueName || <FormattedMessage id='unknownVenue' />}
                        </PageSubtitle>
                        <div css={style.grid}>
                            {sortPitches(pitches, { showPerformance: false })
                                ?.map(pitch => <PitchCardWithData
                                    key={pitch.id}
                                    venue={venue}
                                    pitch={pitch}
                                />)
                            }
                        </div>
                    </React.Fragment>
                })
        }
    </>
}

function BusyStateUI() {
    return <>
        <PageSubtitle>
            <LoaderText />
        </PageSubtitle>
        <div css={style.grid}>
            <PitchCardWithData />
        </div>
    </>
}

function EmptyStateUi() {
    return <FallbackMessage>
        <FormattedMessage id='noPitchesWarn' />
    </FallbackMessage>
}

function PageTitleWithLogo({ club, clubLogo, ...props }) {

    return <PageTitle css={style.title}>
        <NavLink css={style.navLink} to='/'>
            <FormattedMessage id='venues' /> &nbsp;
        </NavLink>
        <span css={css`color: ${colors.main1}; white-space: nowrap;`}>
            {' / '}
            {club?.name || <LoaderText subtle />}
        </span>

        {!club?.logo ? null
            : clubLogo ? <img css={style.logoImg} alt={club.name} src={clubLogo.file} />
                : <Loader size="2em" css={css`margin-left:.5em;`} />
        }
    </PageTitle>
}

export function sortPitches(items, {
    showPerformance,
    selectedTrainingGoal,
    sortedPI = [], // array of ids in desired order (when both flags are true)
    expiredPI = [] // expired results, should be sorted at the end, when showing training goals in performance
}) {

    if (!items) return
    //return items
    // Remember each element's original position
    const originalIndex = new Map(items.map((item, i) => [item.id, i]));
    // Precompute lookups (O(n)) so the comparator stays O(1).
    const piIndex = new Map(sortedPI.map((id, i) => [id, i]));
    const piExpiredIndex = new Map(expiredPI.map((id, i) => [id, i]));

    const byOriginal = (a, b) => (originalIndex.get(a.id) ?? 0) - (originalIndex.get(b.id) ?? 0);

    const cmp = (a, b) => {
        
        // If showPerformance is false OR selectedTrainingGoal is false:
        // sort by ascending pitchSortRank.
        if (!showPerformance || !selectedTrainingGoal) {
            return byOriginal(a, b);
        }

        // showPerformance === true && selectedTrainingGoal === true
        const inA = piIndex.has(a.id);
        const inB = piIndex.has(b.id);
        const inExpA = piExpiredIndex.has(a.id);
        const inExpB = piExpiredIndex.has(b.id);

        if (!inA && !inB && !inExpA && !inExpB) return byOriginal(a, b)

        // All ids in sortedPI come first, the expiredPI, then other
        if (inA && inB) {
            // Among included ids, follow the order in sortedPI
            const ia = piIndex.get(a.id);
            const ib = piIndex.get(b.id);
            if (ia !== ib) return ia - ib;
            // fallback (shouldn't usually hit):
            return byOriginal(a, b);
        }

        if (inExpA && inExpB) {
            //Among excluded ids, follow the order in expiredPI
            const ia = piExpiredIndex.get(a.id);
            const ib = piExpiredIndex.get(b.id);
            if (ia !== ib) return ia - ib;
            // fallback (shouldn't usually hit):
            return byOriginal(a, b);
        }

        if (inA && inExpB) return -1
        if (inExpA && inB) return 1

        if (inA || inExpA) return -1    // b = neither, a always comes first
        if (inB || inExpB) return 1     // a = neither, b always comes first

        return byOriginal(a, b);
    };

    return (Array.prototype.toSorted
        ? items.toSorted(cmp)          // modern, non-mutating
        : items.slice().sort(cmp));    // fallback, still stable in ES2019+ engines
}

const style = {
    pageHeader: css`
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
    `,
    title: css`
        display: flex; 
        align-items: center; 
        max-width: 100%; 
        flex-wrap: wrap;
    `,
    navLink: css`
        text-decoration: none; 
        color: inherit; 
        white-space: nowrap;
    `,
    logoImg: css`
        margin-left: .5em; 
        height: 2em; 
        width: auto;
    `,
    grid: css`
        display: grid;
        grid-gap: 2.5em;
        grid-template-columns: repeat(auto-fill, minmax(23em, 1fr));
        margin-bottom: 2.5em;
    `,
}